/** @jsx jsx */
import React, { type ComponentPropsWithoutRef } from 'react';
import { css, jsx } from '@compiled/react';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import WarningIcon from '@atlaskit/icon/core/migration/warning';
import { Text, Flex, xcss, Box, Stack } from '@atlaskit/primitives';
import { N0, N40, Y300 } from '@atlaskit/theme/colors';
import { token } from '@atlaskit/tokens';
import { expVal } from '@atlassian/jira-feature-experiments';
import { fg } from '@atlassian/jira-feature-gating';
import type { MessageDescriptorV2 as MessageDescriptor } from '@atlassian/jira-intl/src/v2/types.tsx';
import { useIntlV2 as useIntl } from '@atlassian/jira-intl/src/v2/use-intl.tsx';
import { isVisualRefreshEnabled } from '@atlassian/jira-visual-refresh-rollout/src/feature-switch/index.tsx';
import { INTERVAL, ROLLUP, EXPLICIT } from '../../../constants/date.tsx';
import { INCOMING } from '../../../constants/dependency.tsx';
import type { Appearance, OverlayItem } from '../../../types/dependency.tsx';
import Icon from '../icon/index.tsx';
import UnlinkButton from '../unlink-button/index.tsx';
import DependencyCardContainer from './card-container/index.tsx';
import CardDateLabel from './date-label/index.tsx';
import messages from './messages.tsx';

type Props = {
	isBottomCard?: boolean;
	isStacked?: boolean;
	isOverlapping?: boolean;
	unlinkLabel?: string;
} & OverlayItem & {
		appearance: Appearance;
		onUnlink?: (analyticsEvent: UIAnalyticsEvent) => void; // optional handler, controls visibility of the unlink button
	};

const WarningMessage = ({ warningKey, message }: { warningKey: string; message: string }) => (
	<Flex
		gap="space.100"
		testId="roadmap.timeline-table-kit.common.ui.dependency.card.test"
		alignItems="center"
		xcss={warningMessageStyles}
		key={warningKey}
	>
		<WarningIcon color={token('color.icon.warning', Y300)} LEGACY_size="small" label={message} />
		<Text size="small" weight="semibold">
			{message}
		</Text>
	</Flex>
);

const DependenciesCardContent = ({
	appearance,
	isOverlapping = false,
	startDateType,
	dueDateType,
	title,
	iconUrl,
	typeName,
	startDate,
	dueDate,
	onUnlink,
	intervalName,
	warningMessages,
	unlinkLabel,
}: Flow.Diff<
	// @ts-expect-error - TS2344 - Type 'Props' does not satisfy the constraint '{ parent: { title: string; iconUrl: string; } | undefined; }'.
	Props,
	{
		parent: Props['parent'];
		isBottomCard: Props['isBottomCard'];
		isStacked: Props['isStacked'];
	}
>) => {
	const { formatMessage } = useIntl();

	const getCardDateLabelProps = (): JSX.LibraryManagedAttributes<
		typeof CardDateLabel,
		ComponentPropsWithoutRef<typeof CardDateLabel>
	> => {
		let labelProps: { caption: string; noDateMessage: string } = {
			caption: '',
			noDateMessage: '',
		};
		const isIncoming = appearance === INCOMING;
		const dateType = isIncoming ? startDateType : dueDateType;

		switch (dateType) {
			case INTERVAL:
				labelProps = {
					caption: formatMessage(
						isIncoming ? messages.sprintStartDateCaption : messages.sprintEndDateCaption,
					),
					noDateMessage: formatMessage(messages.noSprintAssigned),
				};
				break;

			case ROLLUP:
			case EXPLICIT:
			default:
				labelProps = {
					caption: formatMessage(isIncoming ? messages.startDateCaption : messages.dueDateCaption),
					noDateMessage: formatMessage(
						isIncoming ? messages.noStartDateSet : messages.noDueDateSet,
					),
				};
				break;
		}

		return {
			dateType,
			date: isIncoming ? startDate : dueDate,
			intervalName,
			...labelProps,
		};
	};

	return (
		<>
			<Flex gap="space.050" direction="column">
				<Flex gap="space.100" alignItems="center">
					<Icon
						src={iconUrl || ''}
						alt={formatMessage(
							expVal('issue-terminology-refresh-m2-replace', 'isEnabled', false)
								? messages.iconAltTextIssueTermRefresh
								: messages.iconAltText,
							{ typeName },
						)}
					/>
					<Stack grow="fill">
						<Text size="medium" maxLines={1}>
							{title}
						</Text>
					</Stack>
					{onUnlink && (
						<Box>
							<UnlinkButton onClick={onUnlink} label={unlinkLabel} />
						</Box>
					)}
				</Flex>
				<CardDateLabel {...getCardDateLabelProps()} />
			</Flex>

			{isOverlapping && (
				<WarningMessage
					warningKey={messages.datesOverlapWarning.id}
					message={formatMessage(messages.datesOverlapWarning)}
				/>
			)}

			{warningMessages.map((message: MessageDescriptor) => (
				<WarningMessage key={message.id} warningKey={message.id} message={formatMessage(message)} />
			))}
		</>
	);
};

const DependencyCard = ({ parent, isBottomCard, isStacked, ...props }: Props) => {
	const dependenciesCardContent = <DependenciesCardContent {...props} />;

	const content =
		parent !== undefined ? (
			<>
				<div css={titleContainerStyles}>
					<div css={typeIconContainerStyles}>
						<Icon src={parent.iconUrl} />
					</div>
					<Box paddingBlock="space.075">
						<Text size="UNSAFE_small" maxLines={1} color="color.text.subtlest">
							{parent.title} /
						</Text>
					</Box>
				</div>
				<div css={childContainerStyles}>{dependenciesCardContent}</div>
			</>
		) : (
			dependenciesCardContent
		);

	if (isVisualRefreshEnabled() && fg('visual-refresh_drop_1')) {
		return isStacked ? (
			<Box xcss={[containerStyles, !isBottomCard && withBorderBottomStyles]}>{content}</Box>
		) : (
			<DependencyCardContainer>
				<Box xcss={containerStyles}>{content}</Box>
			</DependencyCardContainer>
		);
	}

	return <div css={containerStylesOld}>{content}</div>;
};

DependencyCard.displayName = 'DependencyCard';

export default DependencyCard;

const containerStylesOld = css({
	display: 'flex',
	flexDirection: 'column',
	position: 'relative',
	paddingTop: token('space.100', '8px'),
	paddingRight: token('space.200', '16px'),
	paddingBottom: token('space.100', '8px'),
	paddingLeft: token('space.200', '16px'),
	backgroundColor: token('elevation.surface.raised', N0),
	boxShadow: token('elevation.shadow.raised', `0 1px ${N40}, 1px 3px 3px 0 rgba(9, 30, 66, 0.13)`),
});

const containerStyles = xcss({
	display: 'flex',
	flexDirection: 'column',
	position: 'relative',
	padding: 'space.150',
});

const withBorderBottomStyles = xcss({
	borderBottom: `1px solid ${token('color.border')}`,
});

const titleContainerStyles = css({
	display: 'flex',
	position: 'relative',
	flexDirection: 'row',
	alignItems: 'center',
	overflow: 'hidden',
});

const typeIconContainerStyles = css({
	display: 'flex',
	marginRight: token('space.100', '8px'),
});

const warningMessageStyles = xcss({
	paddingTop: 'space.100',
});

const childContainerStyles = css({
	position: 'relative',
	paddingLeft: token('space.300', '24px'),
	marginTop: token('space.050', '4px'),
});
